import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInput,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	ZermeloConnectionListItemModel,
	deleteMultiple,
	list
} from "client/Api/ZermeloConfiguration/zermeloConfiguration";
import { SettingsPage } from "../../SettingsPage";
import {
	useSelection,
	useSortHandler,
	useDeepComparison,
	AlertManager,
	useAlerts,
	useUnmountedCancellation,
	createLocalDataSource,
	useWorkspaceMediaQuery,
	MediaQueryKind
} from "client/Hooks";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { contains } from "client/Data/searching";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { ZermeloConnectionDialog } from "./ZermeloConnectionDialog";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { resolveFormattedTextString } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const ZermeloConnectionPage = (): ReactElement => {

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<readonly ZermeloConnectionListItemModel[]>([]);
	const [configureDialogState, setConfigureDialogState] = useState<ConfigureConnectionState>({
		isVisible: false
	});

	const [sortingState, setSortingState] = useState<DataSourceSortableState<ZermeloConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedItems, setSelectedItems] = useState<ReadonlyArray<ZermeloConnectionListItemModel>>([]);

	const bodyRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	useActiveSettingsMenuItem(SettingsPageKind.Zermelo);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [contextMenuHandler, contextMenuProps] = useContextMenu(bodyRef);

	const { sortingFor } = useSortHandler<ZermeloConnectionListItemModel>(
		sortingState,
		setSortingState,
		true
	);

	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm]);

	const { currentItems, pagination } = useDataSource(
		allItems,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 25
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: false
		}
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion, searchTerm);
			setCurrentPageNumber(0);
		},
		[itemsModifiersVersion, searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			bodyRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	useEffect(
		() => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const items: readonly ZermeloConnectionListItemModel[] = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(items);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const deleteHandler = useCallback(
		async (items: readonly ZermeloConnectionListItemModel[]): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-zermelo-connection-confirmation-dialog",
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisConnection")
					: localize("_AreYouSureYouWantToDeleteTheseConnections"),
				confirmText: localize("_Delete")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteMultiple(
					items.map(i => i.id),
					globalCancellation
				);

				setAllItems(
					x => x.filter(
						i => !items.some(di => i.id === di.id)
					)
				);

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ErrorDeletingConnections"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const selectionActions: readonly ConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly ConnectionAction[] = useMemo(
		() => listContextualActions({
			edit: (item) => setConfigureDialogState({ isVisible: true, connectionId: item.id }),
			delete: deleteHandler
		}),
		[deleteHandler]
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ConnectionActionContext> => {
			return getMainAction(
				() => setConfigureDialogState({isVisible: true})
			);
		},
		[]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const afterSaveHandler = useCallback(
		(item: ZermeloConnectionListItemModel): void => {
			setAllItems(x => {
				const copy: ZermeloConnectionListItemModel[] = [...x];
				const index: number = copy.findIndex(i => i.id === item.id);

				if (index === -1) {
					copy.push(item);
				} else {
					copy.splice(
						index,
						1,
						item
					);
				}

				return copy;
			});
		},
		[]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Zermelo")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Zermelo")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_ZermeloConnections")}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInput
							onChange={setSearchTerm}
							trackingId="zermelo-system-search-input"
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[localize("_Name")]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							trackingId="zermelo-system-grid"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								},
								{
									title: localize("_ServerUrl"),
									sortable: sortingFor("serverUrl"),
									trackingId: "server-url"
								}
							]}
							ref={bodyRef}
							height="stretch"
							items={currentItems}
							itemKey="id"
							noResultConfig={noResultConfig}
						>
							{
								item => (
									<TableView.Row
										selectable={selectionFor(item)}
										onExecute={() => setConfigureDialogState({ isVisible: true, connectionId: item.id })}
										onContextMenu={contextMenuHandler}
										trackingId={item.id.toString()}
									>
										<TableView.Cell>{item.name}</TableView.Cell>
										<TableView.Cell>{item.serverUrl}</TableView.Cell>
									</TableView.Row>
								)
							}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{ contextMenuProps && (
							<ContextMenu<ConnectionActionContext>
								{... contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="zermelo-connection-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>

			{configureDialogState.isVisible && (
				<ZermeloConnectionDialog
					connectionId={configureDialogState.connectionId}
					onClose={() => setConfigureDialogState({isVisible: false})}
					onAfterSave={afterSaveHandler}
				/>
			)}

			{confirmationDialog}
		</>
	);
};
ZermeloConnectionPage.displayName = "ZermeloConnectionPage";

type ConfigureConnectionState = {
	isVisible: boolean;
	connectionId?: number;
};

function searchMatchFunction(
	item: ZermeloConnectionListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	if (contains(name, normalizedNotEmptySearchTerm)) {
		return true;
	}

	return false;
}

const useDataSource = createLocalDataSource<ZermeloConnectionListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true
	})
	.getHook();

type ConnectionActionContext = {
	selection: readonly ZermeloConnectionListItemModel[];
	isMobile: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

type ContextualActionsHandlers = {
	edit: (item: ZermeloConnectionListItemModel) => void;
	delete: (items: readonly ZermeloConnectionListItemModel[]) => void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.delete(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

export const zermeloConfigurationRouting = {
	page: "/ZermeloConfiguration"
};